import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Search, ChevronRight, MapPin, Phone, Mail,
  Building2, Briefcase, CreditCard, AlertTriangle
} from 'lucide-react';
import {
  getFilteredClients, getAffairesByClient, getDevisByClient, getFacturesByClient,
  getSocieteById
} from '../data/mockData';
import { useFilters } from '../App';

const ClientsList = () => {
  const filters = useFilters();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('tous');

  const allClients = getFilteredClients(filters);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  const filteredClients = allClients.filter(c => {
    const matchSearch = c.nom.toLowerCase().includes(search.toLowerCase()) ||
      c.ville.toLowerCase().includes(search.toLowerCase()) ||
      c.contactTechnique.nom.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'tous' ||
      (filterType === 'public' && c.marchePublic) ||
      (filterType === 'prive' && !c.marchePublic);
    return matchSearch && matchType;
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><Users size={28} /> Clients</h1>
          <div className="page-meta">
            <span>{allClients.length} clients</span>
            <span>{allClients.filter(c => c.marchePublic).length} marchés publics</span>
            <span>{allClients.filter(c => !c.marchePublic).length} clients privés</span>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="filters-bar">
        <div className="search-input-group">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par nom, ville, contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="tous">Tous les types</option>
          <option value="public">Marchés publics</option>
          <option value="prive">Clients privés</option>
        </select>
      </div>

      {/* Grille des clients */}
      <div className="familles-grid">
        {filteredClients.map(client => {
          const affairesClient = getAffairesByClient(client.id, filters);
          const devisClient = getDevisByClient(client.id, filters);
          const facturesClient = getFacturesByClient(client.id, filters);
          const affairesEnCours = affairesClient.filter(a => a.statut === 'en_cours');
          const facturesEnRetard = facturesClient.filter(f => f.statut === 'en_retard');
          const devisEnAttente = devisClient.filter(d => d.statut === 'envoye');
          const montantTotal = affairesClient.reduce((acc, a) => acc + a.montantHT, 0);

          return (
            <Link to={`/clients/${client.id}`} key={client.id} style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="famille-card">
                <div className="famille-card-header">
                  <div className="famille-avatar" style={{
                    background: client.marchePublic
                      ? 'linear-gradient(135deg, #2563EB, #1E40AF)'
                      : 'linear-gradient(135deg, #F97316, #EA580C)'
                  }}>
                    {client.nom.charAt(0)}
                  </div>
                  <div className="famille-info">
                    <h3>{client.nom}</h3>
                    <p className="famille-responsable">
                      {client.marchePublic ? 'Marché public' : 'Client privé'} — {client.ville}
                    </p>
                  </div>
                  <ChevronRight size={18} style={{ color: 'var(--gray-400)' }} />
                </div>
                <div className="famille-card-body">
                  {/* Sociétés associées */}
                  <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.5rem' }}>
                    {client.societesIds.map(sid => {
                      const s = getSocieteById(sid);
                      return (
                        <span key={sid} className="badge" style={{
                          background: sid === 1 ? '#DBEAFE' : '#FFF7ED',
                          color: sid === 1 ? '#1E40AF' : '#9A3412',
                          fontSize: '0.6875rem'
                        }}>
                          <Building2 size={10} style={{ marginRight: '0.25rem' }} />{s?.nom}
                        </span>
                      );
                    })}
                  </div>

                  <div className="famille-contact">
                    <div className="contact-item">
                      <MapPin size={14} />
                      <span style={{ fontSize: '0.8125rem' }}>{client.adresse}</span>
                    </div>
                    <div className="contact-item">
                      <Phone size={14} />
                      <span>{client.contactTechnique.telephone}</span>
                    </div>
                    <div className="contact-item">
                      <Mail size={14} />
                      <span>{client.contactTechnique.email}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {affairesEnCours.length > 0 && (
                      <span className="badge badge-primary">
                        <Briefcase size={12} /> {affairesEnCours.length} affaire{affairesEnCours.length > 1 ? 's' : ''} en cours
                      </span>
                    )}
                    {devisEnAttente.length > 0 && (
                      <span className="badge badge-warning">
                        {devisEnAttente.length} devis en attente
                      </span>
                    )}
                    {facturesEnRetard.length > 0 && (
                      <span className="badge badge-danger">
                        <AlertTriangle size={12} /> {facturesEnRetard.length} facture{facturesEnRetard.length > 1 ? 's' : ''} en retard
                      </span>
                    )}
                    {affairesClient.length === 0 && (
                      <span className="badge badge-secondary">Aucune affaire</span>
                    )}
                  </div>
                </div>
                <div className="famille-card-footer">
                  <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                    Contact : {client.contactTechnique.nom}
                  </span>
                  <span className="famille-tarif" style={{ fontWeight: 600, color: 'var(--primary)' }}>
                    {formatMontant(montantTotal)}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {filteredClients.length === 0 && (
        <div className="empty-state">
          <Users size={48} />
          <h3>Aucun client trouvé</h3>
          <p>Modifiez vos critères de recherche.</p>
        </div>
      )}
    </div>
  );
};

export default ClientsList;
